import { ColumnSource, RequestFilters, RowSource, CellModel } from "@omegagrid/grid";
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { ToolbarItem } from "@omegagrid/toolbar";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { UserTreeAdapter } from '@reporting/rp-mf-users';
import { DimensionDefinition, SecurityRole } from "@reporting/rp-types/definition";
import { ModuleCode, MODULE_CODES } from "@reporting/rp-types/accounting";
import { FinDropdownAdapter, DimTreeAdapter, PeriodAdapter } from "../adapters";

const PAGE_SIZE = 500;

export class SecurityRoleGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private dims: DimensionDefinition[];

	constructor (
		private module: ModuleCode,
		private accounting: number
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: '_controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			},{
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow,
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'id_security',
			header: '#',
			t: 't',
			e: false,
			w: 75,
		}, {
			data: 'role',
			header: msg('Role'),
			t: 't',
			w: 150,
		}, {
			data: 'negative',
			header: '⛔',
			t: 'b',
			w: 35,
		}, {
			data: 'users',
			header: msg('Users'),
			t: 'm',
			w: 200,
			adapter: Object.assign(
				new UserTreeAdapter(true, { selectMode: 'multiple', autoClose: false, search: true }),
				{ accounting: this.accounting }
			),
		}];
		if (this.module === MODULE_CODES.FINANCE) {
			this._columns.push({
				data: 'dim_r',
				header: msg('Report'),
				t: 'm',
				w: 200,
				adapter: Object.assign(new FinDropdownAdapter('statementsTree', null, { search: true }), { accounting: this.accounting }),
			}, {
				data: 'dim_0',
				header: msg('Period'),
				t: 'm',
				w: 200,
				adapter: Object.assign(new PeriodAdapter(this.module), { accounting: this.accounting })
			});
		}
		this.dims = await rp.ms('definition').get<DimensionDefinition[]>(`/visibleDimensions/${this.module}`, { accounting: this.accounting });
		this.dims.forEach(dim => {
			this._columns.push({
				data: `dim_${dim.dim_num}`,
				header: dim.description,
				t: 'm',
				w: 160,
				adapter: Object.assign(new DimTreeAdapter(this.module, dim.dim_num), { accounting: this.accounting }),
			});
		});
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const rows: RowSource[] = [];
		const roles = await rp.ms('definition').get<SecurityRole[]>(`/security/${this.module}`, Object.assign(
			this.createRequestParams(filters, { count: PAGE_SIZE }),
			{ accounting: this.accounting }
		));
		for (const role of roles) {
			const dims: Record<string | number, any> = {};
			role.dims.forEach(dim => {
				dims[`dim_${dim.dim_num}`] = { v: dim.values, dv: dim.display_value };
			});
			rows.push({
				data: {
					...role,
					role: { v: role.role, s: !role.positive ? { color: "FF0000" } : null },
					users: { v: role.users, dv: role.display_users },
					negative: role.positive != undefined ? !role.positive : false,
					...dims,
				}
			});
		}
		return rows;
	}


	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			editable: true,
			toolbar: {
				new: {
					text: msg('New role'),
					color: ColorTypes.Blue,
					icon: 'plus',
					enabled: true
				},
			},
			customToolbar: [{
				type: 'button',
				id: 'btnActivate',
				icon: { icon: 'right-left-large' },
				text: msg('Activate'),
				color: ColorTypes.Yellow,
			}] as ToolbarItem[],
			rowMenu: {
				deleteRows: true,
			},
		});
	}

	async onAction(action: string, cell: CellModel): Promise<void> {
		switch (action) {
			case 'btnActivate':
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: msg('Are you sure you want to activate security roles?'),
				});
				if (command?.id == 'Y') {
					this.editor.showLoader();
					let result;
					try {
						result = await rp.ms('definition').post(`security/${this.module}/activate`, null, { accounting: this.accounting });
						rp.host.bucket.showAlert({ content: msg('Security roles activated successfully.'), type: 'success' });
					} catch (error) {
						rp.host.bucket.showAlert({ content: msg('Failed to activate security roles.'), type: 'danger' });
						console.error('Failed to activate security roles:', error.data.error);
					}
					this.editor.hideLoader();
				}
				break;
		}
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index}, row.data));
		data.forEach(row => {
            row.positive = !row.negative;
            this._columns.filter(c => ['s', 'm'].includes(c.t)).forEach(col => {
                    const value = row[col.data];
                    if (Array.isArray(value) && value.every(Array.isArray)) {
                        row[col.data] = value.map(([first]) => first);
                    }
                });
        });
		await rp.ms('definition').post(`/security/${this.module}`, data, { accounting: this.accounting });
	}

}
